import { Loader2, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';
import { useAIAnalysis } from '../../hooks/useAIAnalysis';

export function AIAnalysis() {
  const config = useProjectStore((state) => state.config);
  const setActiveTab = useProjectStore((state) => state.setActiveTab);
  const { loading, error, result } = useAIAnalysis();

  const hasResult = !loading && !error && (result || config.designSpec);
  const designSpec = result?.designSpec || config.designSpec;
  const rawAnalysis = result?.rawAnalysis;

  return (
    <div className="max-w-4xl mx-auto pb-10">
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">AI 分析結果</h2>

      {loading && (
        <div className="flex flex-col items-center justify-center py-16 text-slate-600 dark:text-slate-300">
          <Loader2 size={48} className="animate-spin text-blue-600 mb-4" />
          <p className="text-lg font-medium">正在分析參考網站...</p>
          <p className="text-sm mt-2">這可能需要數秒至一分鐘。</p>
        </div>
      )}

      {error && !loading && (
        <div className="p-6 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 text-red-700 dark:text-red-300">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={20} />
            <h3 className="font-semibold">分析失敗</h3>
          </div>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {hasResult && (
        <div className="space-y-6">
          <div className="flex items-center gap-2 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300">
            <CheckCircle size={20} />
            <span className="font-medium">分析完成</span>
          </div>

          {designSpec?.analysis && (
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
              <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-white">設計摘要</h3>
              <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                {designSpec.analysis.summary}
              </p>
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-500 dark:text-slate-400">
                <span>模型：{designSpec.analysis.model}</span>
                <span>信心指數：{Math.round(designSpec.analysis.confidence * 100)}%</span>
                <span>分析網站數：{designSpec.analysis.referencesAnalyzed}</span>
              </div>
            </div>
          )}

          {rawAnalysis && (
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
              <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-white">原始分析</h3>
              <pre className="text-xs md:text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap bg-slate-50 dark:bg-slate-900 p-4 rounded-lg overflow-auto max-h-96">
                {rawAnalysis}
              </pre>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setActiveTab('designspec')}
              className="flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors"
            >
              繼續到 Design Spec 編輯器
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      )}

      {!loading && !error && !hasResult && (
        <div className="text-center py-16 text-slate-500 dark:text-slate-400">
          <p>尚未進行分析。</p>
          <button
            type="button"
            onClick={() => setActiveTab('reference')}
            className="mt-4 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
          >
            前往 Reference 輸入
          </button>
        </div>
      )}
    </div>
  );
}
